// Recuperação de "versão nova publicada".
//
// Todas as páginas são carregadas sob demanda (React.lazy). Depois de um
// deploy, quem ainda está com a versão anterior aberta pede um chunk cujo nome
// (com hash) não existe mais: o import falha e a tela quebra. A saída é
// recarregar uma vez — o index.html novo aponta para os chunks novos.
//
// A trava em sessionStorage evita loop: se o reload não resolver (chunk
// realmente quebrado, rede fora), na segunda falha o erro segue para o
// ErrorBoundary em vez de recarregar para sempre.

const KEY = "isugar_chunk_reload_at";
const WINDOW_MS = 30_000;

// Entre disparar o reload e a página de fato descarregar, o React ainda
// renderiza o erro do import. Esta marca deixa o ErrorBoundary mostrar
// "atualizando" em vez de tratar como crash (e de reportá-lo).
let reloadPending = false;

export const isReloadPending = () => reloadPending;

/** Só para testes: a marca vive no módulo e atravessaria os casos. */
export const resetChunkReloadForTests = () => {
  reloadPending = false;
};

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite (CSS do chunk)
  /ChunkLoadError/i,
];

export const isChunkLoadError = (error) => {
  if (!error) return false;
  const text = `${error.name || ""} ${error.message || error}`;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
};

const readLastReload = () => {
  try {
    return Number(sessionStorage.getItem(KEY)) || 0;
  } catch {
    return 0;
  }
};

/**
 * Recarrega a página, a não ser que isso já tenha acontecido há pouco.
 * @returns {boolean} true se disparou o reload.
 */
export const reloadOnceForNewVersion = (now = Date.now()) => {
  if (now - readLastReload() < WINDOW_MS) return false;
  try {
    sessionStorage.setItem(KEY, String(now));
  } catch {
    // Sem sessionStorage não há trava: melhor não recarregar do que arriscar loop.
    return false;
  }
  reloadPending = true;
  window.location.reload();
  return true;
};

/** Liga a recarga automática aos erros de preload que o Vite emite. */
export const installChunkReload = () => {
  // Sem preventDefault: no Vite 5 ele faz o import() resolver com undefined,
  // e o React.lazy quebra com um TypeError que não parece erro de chunk. O
  // erro original segue para o ErrorBoundary, que vê `isReloadPending()`.
  window.addEventListener("vite:preloadError", () => {
    reloadOnceForNewVersion();
  });
};
